import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { cn } from '../../../utils/cn';


const ComplianceDocumentationStep = ({ data, onNext, onBack }) => {
  const [formData, setFormData] = useState({
    businessLicense: data?.businessLicense || null,
    insuranceCertificate: data?.insuranceCertificate || null,
    taxDocument: data?.taxDocument || null,
    certifications: data?.certifications || null,
    ...data
  });

  const [uploading, setUploading] = useState({});
  const [errors, setErrors] = useState({});

  const documentTypes = [
    {
      key: 'businessLicense',
      label: 'Business License',
      description: 'Valid business operating license',
      required: true,
      icon: 'FileText'
    },
    {
      key: 'insuranceCertificate',
      label: 'Insurance Certificate',
      description: 'Proof of liability insurance coverage',
      required: true,
      icon: 'Shield'
    },
    {
      key: 'taxDocument',
      label: 'Tax Registration',
      description: 'Tax ID or EIN documentation',
      required: true,
      icon: 'Receipt'
    },
    {
      key: 'certifications',
      label: 'Industry Certifications',
      description: 'IATA, ASTA, or other relevant certifications',
      required: false,
      icon: 'Award'
    }
  ];

  const handleFileSelect = async (key, event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    // Validate file size (max 10MB)
    if (file?.size > 10 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, [key]: 'File size must be less than 10MB' }));
      return;
    }

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes?.includes(file?.type)) {
      setErrors(prev => ({ ...prev, [key]: 'Only PDF, JPG, and PNG files are allowed' }));
      return;
    }

    setUploading(prev => ({ ...prev, [key]: true }));
    setErrors(prev => ({ ...prev, [key]: null }));

    try {
      // Simulate file upload (in real implementation, upload to Supabase Storage)
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setFormData(prev => ({
        ...prev,
        [key]: {
          name: file?.name,
          size: file?.size,
          type: file?.type,
          uploadedAt: new Date()?.toISOString()
        }
      }));
    } catch (error) {
      setErrors(prev => ({ ...prev, [key]: 'Upload failed. Please try again.' }));
    } finally {
      setUploading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleRemoveFile = (key) => {
    setFormData(prev => ({ ...prev, [key]: null }));
  };

  const validate = () => {
    const newErrors = {};
    documentTypes?.forEach(doc => {
      if (doc?.required && !formData?.[doc?.key]) {
        newErrors[doc.key] = `${doc?.label} is required`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validate()) {
      onNext?.(formData);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024)?.toFixed(1) + ' KB';
    return (bytes / (1024 * 1024))?.toFixed(1) + ' MB';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <h2 className="text-2xl font-bold text-foreground">Compliance & Documentation</h2>
        <p className="text-muted-foreground">
          Upload required documents for verification. All files must be in PDF, JPG, or PNG format (max 10MB each).
        </p>
      </div>
      {/* Document Upload Sections */}
      <div className="space-y-4">
        {documentTypes?.map((doc) => (
          <div key={doc?.key} className="glass-container rounded-xl p-6">
            <div className="space-y-4">
              {/* Document Header */}
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon name={doc?.icon} size={20} className="text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground">
                      {doc?.label}
                      {doc?.required && <span className="text-destructive ml-1">*</span>}
                    </h4>
                    <p className="text-sm text-muted-foreground">{doc?.description}</p>
                  </div>
                </div>
              </div>

              {/* Upload Area */}
              {!formData?.[doc?.key] ? (
                <div>
                  <label
                    htmlFor={`file-${doc?.key}`}
                    className={cn(
                      'flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors',
                      'hover:bg-white/5',
                      errors?.[doc?.key] ? 'border-destructive' : 'border-white/20'
                    )}
                  >
                    {uploading?.[doc?.key] ? (
                      <div className="flex flex-col items-center gap-2">
                        <Icon name="Loader2" size={24} className="text-primary animate-spin" />
                        <p className="text-sm text-muted-foreground">Uploading...</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <Icon name="Upload" size={24} className="text-muted-foreground" />
                        <p className="text-sm text-foreground font-medium">Click to upload or drag and drop</p>
                        <p className="text-xs text-muted-foreground">PDF, JPG, PNG (max 10MB)</p>
                      </div>
                    )}
                    <input
                      id={`file-${doc?.key}`}
                      type="file"
                      className="hidden"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleFileSelect(doc?.key, e)}
                      disabled={uploading?.[doc?.key]}
                    />
                  </label>
                  {errors?.[doc?.key] && (
                    <p className="text-sm text-destructive mt-2">{errors?.[doc?.key]}</p>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-between p-4 bg-success/10 border border-success/20 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Icon name="FileCheck" size={20} className="text-success" />
                    <div>
                      <p className="text-sm font-medium text-foreground">{formData?.[doc?.key]?.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatFileSize(formData?.[doc?.key]?.size)} • Uploaded
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="X"
                    onClick={() => handleRemoveFile(doc?.key)}
                  >
                    Remove
                  </Button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      {/* Info Box */}
      <div className="glass-container rounded-xl p-6 bg-warning/5">
        <div className="flex items-start gap-3">
          <Icon name="AlertTriangle" size={20} className="text-warning mt-0.5" />
          <div className="space-y-2">
            <h4 className="font-semibold text-foreground">Document Verification</h4>
            <p className="text-sm text-muted-foreground">
              All uploaded documents will be reviewed by our compliance team. Verification typically takes 2-3 business days. You'll receive email notifications about your application status.
            </p>
          </div>
        </div>
      </div>
      {/* Actions */}
      <div className="flex justify-between pt-6 border-t border-white/20">
        <Button
          variant="outline"
          iconName="ArrowLeft"
          onClick={onBack}
        >
          Back
        </Button>
        <Button
          iconName="ArrowRight"
          iconPosition="right"
          onClick={handleNext}
        >
          Continue to Review
        </Button>
      </div>
    </div>
  );
};

export default ComplianceDocumentationStep;